import React, { useState, useMemo } from 'react';
import { PriceChange, PRICE_TYPE_LABELS } from '../../types';
import './Table.scss';
import TableDateFormatter from '../../../../../../components/common/Table/TableDateFormatter';
import { HiChevronUp, HiChevronDown, HiChevronUpDown } from 'react-icons/hi2';

interface TableProps {
  data: PriceChange[];
}

type SortKey = 'product_name' | 'user_name' | 'price_type' | 'old_price' | 'new_price' | 'change' | 'timestamp';
type SortDirection = 'asc' | 'desc' | null;

const PRICE_TYPE_BADGE: Record<string, string> = {
  restaurant: 'mp-badge-warning',
  delivery: 'mp-badge-info',
  online: 'mp-badge-purple',
  desk: 'mp-badge-success',
  all: 'mp-badge-neutral',
};

const Table: React.FC<TableProps> = ({ data }) => {
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  const formatCurrency = (value: number): string =>
    `$${Math.round(value).toLocaleString('es-CL')}`;

  const calculateChange = (oldPrice: number, newPrice: number): number =>
    newPrice - oldPrice;

  const calculatePercentage = (oldPrice: number, newPrice: number): string => {
    if (oldPrice === 0) return '-';
    const pct = ((newPrice - oldPrice) / oldPrice) * 100;
    return `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`;
  };

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      if (sortDirection === 'asc') setSortDirection('desc');
      else if (sortDirection === 'desc') { setSortKey(null); setSortDirection(null); }
      else setSortDirection('asc');
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  const sortedData = useMemo(() => {
    if (!sortKey || !sortDirection) return data;
    return [...data].sort((a, b) => {
      let aVal: any, bVal: any;
      switch (sortKey) {
        case 'product_name': aVal = a.product_name?.toLowerCase() || ''; bVal = b.product_name?.toLowerCase() || ''; break;
        case 'user_name': aVal = a.user_name?.toLowerCase() || ''; bVal = b.user_name?.toLowerCase() || ''; break;
        case 'price_type': aVal = a.price_type; bVal = b.price_type; break;
        case 'old_price': aVal = a.old_price; bVal = b.old_price; break;
        case 'new_price': aVal = a.new_price; bVal = b.new_price; break;
        case 'change': aVal = a.new_price - a.old_price; bVal = b.new_price - b.old_price; break;
        case 'timestamp': aVal = new Date(a.timestamp).getTime(); bVal = new Date(b.timestamp).getTime(); break;
        default: return 0;
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, sortKey, sortDirection]);

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return <HiChevronUpDown size={14} className="opacity-30 ml-1" />;
    if (sortDirection === 'asc') return <HiChevronUp size={14} className="ml-1" style={{ color: '#FC7556' }} />;
    return <HiChevronDown size={14} className="ml-1" style={{ color: '#FC7556' }} />;
  };

  if (data.length === 0) {
    return (
      <div className="mp-table-card">
        <div className="mp-empty-state">
          <span className="mp-empty-state-title">No hay cambios de precios</span>
          <p className="text-sm opacity-50" style={{ color: 'var(--text-color)' }}>
            No se encontraron cambios para el período seleccionado
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mp-table-card">
      <div style={{ overflowX: 'auto' }}>
        <table className="mp-table price-changes-table">
          <thead className="mp-table-head">
            <tr>
              <th className="cursor-pointer select-none" onClick={() => handleSort('product_name')}>
                <span className="inline-flex items-center">Producto <SortIcon col="product_name" /></span>
              </th>
              <th className="cursor-pointer select-none hidden md:table-cell" onClick={() => handleSort('user_name')}>
                <span className="inline-flex items-center">Usuario <SortIcon col="user_name" /></span>
              </th>
              <th className="cursor-pointer select-none hidden md:table-cell" onClick={() => handleSort('price_type')}>
                <span className="inline-flex items-center">Tipo <SortIcon col="price_type" /></span>
              </th>
              <th className="cursor-pointer select-none" onClick={() => handleSort('old_price')}>
                <span className="inline-flex items-center">Precio Ant. <SortIcon col="old_price" /></span>
              </th>
              <th className="cursor-pointer select-none" onClick={() => handleSort('new_price')}>
                <span className="inline-flex items-center">Precio Nuevo <SortIcon col="new_price" /></span>
              </th>
              <th className="cursor-pointer select-none" onClick={() => handleSort('change')}>
                <span className="inline-flex items-center">Cambio <SortIcon col="change" /></span>
              </th>
              <th className="cursor-pointer select-none" onClick={() => handleSort('timestamp')}>
                <span className="inline-flex items-center">Fecha <SortIcon col="timestamp" /></span>
              </th>
            </tr>
          </thead>
          <tbody className="mp-table-body">
            {sortedData.map((change, index) => {
              const priceChange = calculateChange(change.old_price, change.new_price);
              const isIncrease = priceChange > 0;
              const isDecrease = priceChange < 0;

              return (
                <tr key={`${change.product_id}-${change.timestamp}-${index}`}>
                  <td style={{ fontWeight: 500 }}>{change.product_name || 'N/A'}</td>
                  <td className="hidden md:table-cell">
                    {change.user_name || <span className="opacity-40 italic">N/A</span>}
                  </td>
                  <td className="hidden md:table-cell">
                    <span className={`mp-badge ${PRICE_TYPE_BADGE[change.price_type] || 'mp-badge-neutral'}`}>
                      {PRICE_TYPE_LABELS[change.price_type] || change.price_type}
                    </span>
                  </td>
                  <td className="price-cell-muted">{formatCurrency(change.old_price)}</td>
                  <td style={{ fontWeight: 500 }}>{formatCurrency(change.new_price)}</td>
                  <td>
                    <div className={`change-badge ${isIncrease ? 'increase' : isDecrease ? 'decrease' : 'neutral'}`}>
                      <span className="change-arrow">{isIncrease ? '↑' : isDecrease ? '↓' : '–'}</span>
                      <span className="change-value">{formatCurrency(Math.abs(priceChange))}</span>
                      <span className="change-pct">{calculatePercentage(change.old_price, change.new_price)}</span>
                    </div>
                  </td>
                  <td>
                    <TableDateFormatter data={change.timestamp} isRealTime={false} stopCounting={true} isTimestamp={true} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Table;
